import React, { useEffect, useState } from "react";
import { ExternalLink, Play, X } from "lucide-react";
import { getInstagramCities, getInstagramConsultants, getInstagramReels } from "./api";
import "./reels.css";

const displayDate = (value) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium" }).format(
        new Date(value),
      )
    : "";

const reelText = (caption = "") => {
  const cleaned = caption.replace(/\s+/g, " ").trim();
  if (!cleaned) return { title: "Baran Emlak videosu", description: "" };
  const [firstPart, ...rest] = cleaned.split(/[.!?]\s+|\n+/);
  return {
    title: firstPart.slice(0, 72) || "Baran Emlak videosu",
    description: rest.join(" ").slice(0, 120),
  };
};

export function ReelsPage() {
  const [data, setData] = useState(null);
  const [cities, setCities] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [cityId, setCityId] = useState("");
  const [consultantId, setConsultantId] = useState("");
  const [selectionMode, setSelectionMode] = useState("city");
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  useEffect(() => {
    let alive = true;
    getInstagramCities()
      .then((next) => {
        if (!alive) return;
        const availableCities = next.cities || [];
        setCities(availableCities);
        const aydin = availableCities.find((city) => city.name === "Aydın");
        setCityId(String((aydin || availableCities[0] || {}).id || ""));
      })
      .catch((reason) => alive && setError(reason.message));
    getInstagramConsultants()
      .then((next) => alive && setConsultants(next.consultants || []))
      .catch(() => alive && setConsultants([]));
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (selectionMode === "city" && !cityId) return;
    if (selectionMode === "consultant" && !consultantId) return;
    let alive = true;
    setData(null);
    setError("");
    getInstagramReels(selectionMode === "city" ? cityId : "", selectionMode === "consultant" ? consultantId : "")
      .then((next) => alive && setData(next))
      .catch((reason) => alive && setError(reason.message));
    return () => {
      alive = false;
    };
  }, [cityId, consultantId, selectionMode]);
  return (
    <main>
      <section className="page-title reels-title">
        <div className="shell">
          <p className="eyebrow">@AYDIN_BARAN_EMLAK</p>
          <h1>Reels</h1>
          <p>Portföylerimizi ve güncel videolarımızı keşfedin.</p>
        </div>
      </section>
      <section className="shell section reels-page">
        {(cities.length > 0 || consultants.length > 0) && (
          <div className="reels-city-picker">
            <div>
              <span>Reels akışı</span>
              <strong>{selectionMode === "city" ? "Şehir / ilçe seçin" : "Danışman seçin"}</strong>
            </div>
            <label>
              <span className="sr-only">Akış türü</span>
              <select value={selectionMode} onChange={(event) => setSelectionMode(event.target.value)}>
                <option value="city">Şehir / İlçe</option>
                <option value="consultant">Danışman</option>
              </select>
            </label>
            <label>
              <span className="sr-only">Akış seçimi</span>
              {selectionMode === "city" ? <select value={cityId} onChange={(event) => setCityId(event.target.value)}>
                {cities.map((city) => <option key={`${city.id}-${city.district_id || "all"}`} value={city.id}>
                  {city.district_name || city.name}
                </option>)}
              </select> : <select value={consultantId} onChange={(event) => setConsultantId(event.target.value)}>
                {consultants.map((consultant) => <option key={consultant.user_id} value={consultant.user_id}>
                  {consultant.name} (@{consultant.username})
                </option>)}
              </select>}
            </label>
          </div>
        )}
        {!data && !error ? (
          <p className="loading">Reels yükleniyor…</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : !data.configured ? (
          <div className="reels-empty">
            <h2>Reels akışı hazırlanıyor</h2>
            <p>
              Instagram hesabımız bağlandığında yeni videolar burada otomatik
              görünecek.
            </p>
          </div>
        ) : data.reels?.length ? (
          <div className="reels-grid">
            {data.reels.map((reel) => {
              const { title, description } = reelText(reel.caption);
              return (
                <article className="reel-card" key={reel.id || reel.permalink}>
                <button
                  type="button"
                  onClick={() => setActive(reel)}
                  aria-label="Reels videosunu izle"
                >
                  {reel.thumbnail_url ? (
                    <img
                      src={reel.thumbnail_url}
                      loading="lazy"
                      decoding="async"
                      alt="Instagram Reels önizlemesi"
                    />
                  ) : (
                    <div className="reel-placeholder" />
                  )}
                  <span className="reel-play">
                    <Play fill="currentColor" /> İzle
                  </span>
                </button>
                  <div className="reel-card-content">
                    <span className="reel-label">Instagram Reels</span>
                    <h2>{title}</h2>
                    {description && <p>{description}</p>}
                    <small>{displayDate(reel.timestamp)}</small>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="reels-empty">
            <h2>Henüz Reel bulunmuyor</h2>
            <p>Yeni videolar yayımlandığında burada görünecek.</p>
          </div>
        )}
        {active && (
          <div
            className="reel-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Instagram Reels videosu"
          >
            <button
              type="button"
              className="reel-modal-close"
              onClick={() => setActive(null)}
              aria-label="Videoyu kapat"
            >
              <X />
            </button>
            <iframe
              src={active.embed_url}
              title="Instagram Reels"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share; fullscreen"
              allowFullScreen
            />
            <a href={active.permalink} target="_blank" rel="noreferrer">
              Instagram’da aç <ExternalLink />
            </a>
          </div>
        )}
      </section>
    </main>
  );
}
